/**
 * Keep in sync with trovepile-firebase-master/functions/src/utils/aiUsageTokens.ts
 * (display-only rollups for the AI usage settings table).
 */

export type UsageTokenFields = {
  embedPromptTokens?: number;
  embedTotalTokens?: number;
  chatPromptTokens?: number;
  chatCompletionTokens?: number;
  chatTotalTokens?: number;
};

function num(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const x = Number(v);
    if (Number.isFinite(x)) return x;
  }
  return 0;
}

export function usageFromRowData(row: Record<string, unknown>): UsageTokenFields {
  const u = (row.usage ?? {}) as Record<string, unknown>;
  return {
    embedPromptTokens: num(u.embedPromptTokens),
    embedTotalTokens: num(u.embedTotalTokens),
    chatPromptTokens: num(u.chatPromptTokens),
    chatCompletionTokens: num(u.chatCompletionTokens),
    chatTotalTokens: num(u.chatTotalTokens),
  };
}

export function rollupEmbedTokens(usage: UsageTokenFields): number {
  if (typeof usage.embedTotalTokens === 'number') {
    return Math.max(0, usage.embedTotalTokens);
  }
  if (typeof usage.embedPromptTokens === 'number') {
    return Math.max(0, usage.embedPromptTokens);
  }
  return 0;
}

export function rollupChatTokens(usage: UsageTokenFields): number {
  const p = usage.chatPromptTokens ?? 0;
  const c = usage.chatCompletionTokens ?? 0;
  if (p > 0 || c > 0) return p + c;
  if (typeof usage.chatTotalTokens === 'number') {
    return Math.max(0, usage.chatTotalTokens);
  }
  return 0;
}

export function tokenPartsFromRow(row: Record<string, unknown>): { embed: number; chat: number } {
  const disp = row.usageDisplay as { embed?: unknown; chat?: unknown } | undefined;
  if (
    disp &&
    typeof disp.embed === 'number' &&
    typeof disp.chat === 'number' &&
    Number.isFinite(disp.embed) &&
    Number.isFinite(disp.chat)
  ) {
    return { embed: disp.embed, chat: disp.chat };
  }
  const usage = usageFromRowData(row);
  return { embed: rollupEmbedTokens(usage), chat: rollupChatTokens(usage) };
}

export function estimatedUsdFromRow(row: Record<string, unknown>): number {
  const v = row.estimatedUsd;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const x = Number(v);
    if (Number.isFinite(x)) return x;
  }
  return 0;
}
